"use client";

import { FormEvent, useMemo, useState } from "react";
import { signIn } from "next-auth/react";
import Image from "next/image";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/features/auth/components/password-input";
import { Label } from "@/components/ui/label";

const errorMessages: Record<string, string> = {
  CredentialsSignin: "Sai tài khoản hoặc mật khẩu",
  Default: "Đăng nhập không thành công",
};

const appName = process.env.NEXT_PUBLIC_APP_NAME || "Martech Payroll";

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { locale } = useParams<{ locale: string }>();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectPath = useMemo(() => {
    const returnUrl = searchParams.get("return-url") || "/";
    const normalizedReturnUrl = returnUrl.startsWith("/") ? returnUrl : "/";
    return normalizedReturnUrl === "/" ? `/${locale}` : `/${locale}${normalizedReturnUrl}`;
  }, [locale, searchParams]);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSigningIn) return;

    setError(null);
    setIsSigningIn(true);

    const result = await signIn("credentials", {
      username,
      password,
      redirect: false,
    });

    setIsSigningIn(false);

    if (!result || result.error) {
      setError(
        errorMessages[result?.error || "Default"] ||
          result?.error ||
          errorMessages.Default
      );
      return;
    }

    router.replace(redirectPath);
    router.refresh();
  };

  return (
    <Card className="overflow-hidden border-white/60 bg-white/90 shadow-overlay backdrop-blur xl:min-h-[620px] dark:border-slate-700/60 dark:bg-slate-900/90">
      <CardContent className="grid p-0 lg:grid-cols-[1.08fr_0.92fr]">
        <section className="relative hidden overflow-hidden bg-[linear-gradient(160deg,#2449b4_0%,#2d61d2_45%,#5f95ff_100%)] p-10 text-white lg:flex lg:flex-col lg:justify-between">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.16),transparent_28%)]" />
          <div className="relative space-y-16">
            <div className="flex items-center gap-3">
              <div className="flex size-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur">
                <Image
                  src="/images/logo.png"
                  alt={appName}
                  width={42}
                  height={42}
                  className="brightness-0 invert"
                  priority
                />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                  Internal Access
                </p>
                <h1 className="text-2xl font-semibold tracking-tight text-white">
                  {appName}
                </h1>
              </div>
            </div>

            <div className="max-w-md space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur">
                <ShieldCheck className="h-4 w-4" />
                Khu vực quản trị nội bộ
              </div>
              <div className="space-y-4">
                <h2 className="text-4xl font-semibold leading-tight text-white">
                  Quản lý lương thưởng với trải nghiệm gọn gàng hơn.
                </h2>
                <p className="max-w-md text-base leading-7 text-white/78">
                  Đăng nhập để tiếp tục truy cập hệ thống, theo dõi dữ liệu và xử lý tác vụ quản trị hằng ngày.
                </p>
              </div>
            </div>
          </div>

        </section>

        <section className="flex items-center justify-center bg-white/88 px-5 py-8 sm:px-8 sm:py-10 lg:px-10 xl:px-12 dark:bg-slate-900/88">
          <div className="w-full max-w-md space-y-8">
            <div className="space-y-5 lg:hidden">
              <div className="flex items-center gap-3">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-primaryAlt/10 ring-1 ring-primaryAlt/10">
                  <Image src="/images/logo.png" alt={appName} width={34} height={34} priority />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.26em] text-primaryAlt/70">
                    Internal Access
                  </p>
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                    {appName}
                  </h1>
                </div>
              </div>
              <p className="max-w-sm text-sm leading-6 text-slate-500 dark:text-slate-400">
                Đăng nhập để tiếp tục sử dụng hệ thống quản trị payroll.
              </p>
            </div>

            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-primaryAlt/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-primaryAlt/80">
                Sign In
              </div>
              <div className="space-y-3">
                <h2 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  Chào mừng quay lại
                </h2>
                <p className="text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Sử dụng tài khoản quản trị để tiếp tục truy cập vào hệ thống.
                </p>
              </div>
            </div>

            <form className="space-y-5" onSubmit={handleLogin}>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Tên đăng nhập
                </Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  disabled={isSigningIn}
                  required
                  className="focus-ring-subtle h-12 rounded-xl border-slate-200 bg-white/90 px-4 text-base shadow-sm shadow-slate-200/60 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800/90 dark:text-slate-100 dark:placeholder:text-slate-500"
                  placeholder="Nhập tên đăng nhập"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Mật khẩu
                </Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  disabled={isSigningIn}
                  required
                  className="focus-ring-subtle h-12 rounded-xl border-slate-200 bg-white/90 px-4 text-base shadow-sm shadow-slate-200/60 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800/90 dark:text-slate-100 dark:placeholder:text-slate-500"
                  placeholder="Nhập mật khẩu"
                />
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="h-12 w-full rounded-xl bg-primaryAlt/95 text-base font-semibold text-white shadow-[0_16px_28px_rgba(37,79,191,0.24)] transition duration-200 hover:bg-primaryAlt/85 hover:shadow-[0_20px_32px_rgba(37,79,191,0.28)] active:bg-primaryAlt/85 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isSigningIn}
              >
                {isSigningIn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Đang đăng nhập...
                  </>
                ) : (
                  <>
                    Đăng nhập
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="border-t border-slate-200/80 pt-5 text-center text-xs text-slate-400 dark:border-slate-700/80 dark:text-slate-500">
              © Martech
            </div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
